'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, Checkbox, Image, Sheet, Typography } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type {
  CurationSnapDetailGoodsItem,
  CurationSnapDetailItem,
  GetMySnapsServiceResponse,
} from '@musinsa/services-curator';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { VirtuosoGrid } from 'react-virtuoso';
import { twMerge } from 'tailwind-merge';
import { usePreventBodyScroll } from '../common/hooks';
import { curatorQueries } from '../query-factory';
import { SnapTaggedGoodsSheetContent } from '../snap-detail/components/SnapTaggedGoodsRow/SnapTaggedGoodsSheetContent';
import { SelectedSnapsWithButton } from './SelectedSnapsWithButton';
import { snapItemFromSnapDetailApi } from './snapCurationMappers';
import type { ShopSnapCurationEditFormData, SnapCurationListItem } from './type';

const MAX_SELECTED_SNAPS = 100;

const SNAP_GRID_LIST_CLASS =
  '[&_.snap-grid-list]:grid [&_.snap-grid-list]:grid-cols-3 [&_.snap-grid-list]:gap-0 [&_.snap-grid-list]:bg-neutral-color-white';

function normalizeSnapId(item: CurationSnapDetailItem | SnapCurationListItem) {
  return String(item.id);
}

function isSnapSelected(item: CurationSnapDetailItem, selected: SnapCurationListItem[]) {
  const id = normalizeSnapId(item);
  return selected.some((s) => normalizeSnapId(s) === id);
}

function getNextPageParamFromSnapsPage(lastPage: GetMySnapsServiceResponse) {
  return lastPage.pagination.hasNextPage && lastPage.pagination.nextPage != null
    ? lastPage.pagination.nextPage
    : undefined;
}

interface Props {
  className?: string;
  /** 등록/수정 공통 — `GET /v2/curations/snaps`(큐레이터 본인 업로드 스냅 전체) */
  defaultCheckedSnaps: SnapCurationListItem[];
  onSubmit: () => void;
}

export function SelectSnaps({ className, defaultCheckedSnaps, onSubmit }: Props) {
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const { control, setValue } = useFormContext<ShopSnapCurationEditFormData>();
  const checkedSnaps =
    useWatch({
      control,
      name: 'selectedSnaps',
    }) ??
    defaultCheckedSnaps ??
    [];
  const [tagSheetOpen, setTagSheetOpen] = useState(false);
  const [tagSheetGoods, setTagSheetGoods] = useState<CurationSnapDetailGoodsItem[]>([]);

  const mySnapsQueryKey = curatorQueries.curations.mySnaps({ size: 30 }).queryKey;

  const invalidateMySnaps = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: mySnapsQueryKey });
  }, [mySnapsQueryKey, queryClient]);

  usePreventBodyScroll({ open: tagSheetOpen });

  const snapsQuery = useInfiniteQuery({
    ...curatorQueries.curations.mySnaps({ size: 30 }),
    initialPageParam: 1,
    getNextPageParam: getNextPageParamFromSnapsPage,
  });

  const fetchNextData = useCallback(() => {
    if (snapsQuery.hasNextPage && !snapsQuery.isFetchingNextPage) {
      snapsQuery.fetchNextPage();
    }
  }, [snapsQuery]);

  const snaps = useMemo(
    () => snapsQuery.data?.pages.flatMap((page) => page.list) ?? [],
    [snapsQuery.data],
  );

  const syncCheckedSnaps = useCallback(
    (snaps: SnapCurationListItem[]) => {
      setValue('selectedSnaps', snaps, {
        shouldDirty: true,
        shouldValidate: false,
      });
    },
    [setValue],
  );

  const onToggleSnap = useCallback(
    (item: CurationSnapDetailItem) => {
      const id = normalizeSnapId(item);
      const already = isSnapSelected(item, checkedSnaps);
      if (already) {
        syncCheckedSnaps(checkedSnaps.filter((s) => normalizeSnapId(s) !== id));
        return;
      }
      if (checkedSnaps.length >= MAX_SELECTED_SNAPS) {
        dialog.open({
          title: `최대 ${MAX_SELECTED_SNAPS}개의 스냅을 선택할 수 있습니다.`,
          confirmButtonText: '확인',
          onConfirm: () => dialog.close(),
        });
        return;
      }
      if (service === SERVICE.MUSINSA && curatorId) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'snap_select',
          component_name: 'button',
          event_name: 'click_button',
          from: getScreenName(),
          id: curatorId,
          section_name: 'curator_curation_create',
          type: 'snap',
        });
      }
      syncCheckedSnaps([...checkedSnaps, snapItemFromSnapDetailApi(item)]);
    },
    [checkedSnaps, curatorId, dialog, service, syncCheckedSnaps],
  );

  if (snapsQuery.isPending) {
    return (
      <div className={twMerge('flex flex-1 items-center justify-center p-8', className)}>
        <Typography colorScheme="gray-600" variant="body14pxReg">
          불러오는 중…
        </Typography>
      </div>
    );
  }

  if (snapsQuery.isError) {
    return (
      <div className={twMerge('flex flex-1 items-center justify-center p-8', className)}>
        <Typography colorScheme="gray-600" variant="body14pxReg">
          스냅 목록을 불러오지 못했습니다.
        </Typography>
      </div>
    );
  }

  return (
    <>
      <Sheet direction="bottom" open={tagSheetOpen} onOpenChange={setTagSheetOpen}>
        <SnapTaggedGoodsSheetContent
          goodsItems={tagSheetGoods}
          invalidateSnapDetail={invalidateMySnaps}
          onOpenChange={setTagSheetOpen}
        />
      </Sheet>
      <div
        className={twMerge(
          'flex w-full grow flex-col pb-[140px]!',
          SNAP_GRID_LIST_CLASS,
          className,
        )}
      >
        <VirtuosoGrid
          useWindowScroll
          data={snaps}
          endReached={fetchNextData}
          listClassName="snap-grid-list"
          itemContent={(_index, item) => {
            const selected = isSnapSelected(item, checkedSnaps);
            const thumb = item.imageUrls[0] ?? '';
            const goods = item.goods ?? [];
            const tagCount = goods.length;

            return (
              <div className="flex w-full flex-col bg-white">
                <div
                  role="button"
                  tabIndex={0}
                  className="relative flex w-full cursor-pointer flex-col"
                  onClick={() => onToggleSnap(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggleSnap(item);
                    }
                  }}
                >
                  <div className="relative aspect-[5/6] w-full">
                    {thumb ? (
                      <Image
                        src={thumb}
                        alt=""
                        className="size-full object-cover"
                        width={5}
                        height={6}
                      />
                    ) : (
                      <div className="size-full bg-gray-200" />
                    )}
                    <div className="pointer-events-none absolute right-2 top-2 z-[10]">
                      <Checkbox checked={selected} />
                    </div>
                  </div>
                </div>
                {tagCount > 0 && (
                  <div className="border-gray-100 border-t px-1 py-1">
                    <Button
                      type="button"
                      variant="secondary"
                      size={32}
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (service === SERVICE.MUSINSA && curatorId) {
                          amplitudeSendEvent({
                            action_name: 'click',
                            button_name: 'check_tagging_goods',
                            component_name: 'button',
                            event_name: 'click_button',
                            from: getScreenName(),
                            id: curatorId,
                            section_name: 'curator_curation_create',
                            type: 'snap',
                          });
                        }
                        setTagSheetGoods(goods);
                        setTagSheetOpen(true);
                      }}
                    >
                      <Typography variant="body13pxMed">{`태그 상품 ${tagCount}개`}</Typography>
                    </Button>
                  </div>
                )}
              </div>
            );
          }}
        />
        <SelectedSnapsWithButton
          selectedSnaps={checkedSnaps}
          onUnselect={(removedSnapId) =>
            syncCheckedSnaps(
              checkedSnaps.filter((s) => normalizeSnapId(s) !== String(removedSnapId)),
            )
          }
          onDragEnd={syncCheckedSnaps}
          onSubmit={onSubmit}
        />
      </div>
    </>
  );
}
